/**
 * ClaudeCredentialModal — paste-token form for a Claude credential used to
 * deploy a managed app.
 *
 * CAPTURE-ONLY (2026-06-15 credential-scoping fix): this modal NEVER
 * persists the token to any account-level store. Agent credentials are
 * PER-PROJECT now — the durable home is the project's AI Settings
 * (`secrets.aiOAuthToken` / `secrets.aiApiKey`, managed in Project
 * Settings). This modal exists only to capture a token for a SINGLE deploy
 * when the selected project has no AI credential yet: it validates the
 * shape locally and hands the plaintext back to the caller via
 * `onCapture({ token, mode })`. The caller injects it into the deploy POST
 * body as the vendor-neutral `agentToken` (per-app, ephemeral) and drops it
 * immediately. Nothing is written to the account store or the project row.
 *
 * Two input choices via a radio:
 *   - OAuth token (preferred — bills against the user's Claude Code
 *     subscription, minted via `claude setup-token`).
 *   - Anthropic API key (`sk-ant-api…` — bills against API credits).
 *
 * Uses the app's shared <MktAlertModal> (same `mkt-modal` shell as the app
 * deploy/preview modal + every other marketplace dialog) for the chrome.
 *
 * Props: { isOpen, onClose, onCapture({ token, mode }), defaultKind }
 */

import React, { useEffect, useState } from 'react';
import MktAlertModal from '../molecules/MktAlertModal/MktAlertModal';
import PillButton from '../shared/PillButton';

const inputStyle = {
  width: '100%', padding: '10px 12px', borderRadius: 6,
  border: '1px solid rgba(255,255,255,0.15)', background: 'rgba(255,255,255,0.04)',
  color: '#fff', fontSize: 14, boxSizing: 'border-box',
  // Kill the browser's default focus ring — it stacked on top of our own
  // border and read as a double border. Focus is shown by recoloring the
  // single existing border (see `focused` below).
  outline: 'none',
};

const radioCard = (active, disabled) => ({
  flex: 1, cursor: disabled ? 'not-allowed' : 'pointer', padding: 10, borderRadius: 6,
  border: `1px solid ${active ? 'var(--ion-color-primary)' : 'rgba(255,255,255,0.15)'}`,
  background: active ? 'rgba(99,102,241,0.12)' : 'transparent',
});

export default function ClaudeCredentialModal({ isOpen, onClose, onCapture, defaultKind = 'oauth' }) {
  const [kind, setKind] = useState(defaultKind);
  const [token, setToken] = useState('');
  const [error, setError] = useState('');
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (isOpen) { setKind(defaultKind); setToken(''); setError(''); }
  }, [isOpen, defaultKind]);

  // Best-effort auto-detect on paste — nudge the radio if the token format
  // clearly matches the other kind.
  const handleTokenChange = (next) => {
    setToken(next);
    setError('');
    const lower = (next || '').trim();
    if (/^sk-ant-oat/i.test(lower) || /^oat_/i.test(lower)) { if (kind !== 'oauth') setKind('oauth'); }
    else if (/^sk-ant-api/i.test(lower)) { if (kind !== 'api') setKind('api'); }
  };

  const handleSubmit = () => {
    const trimmed = token.trim();
    if (!trimmed) { setError('Token is required.'); return; }
    // Capture-only: validate the shape locally + hand the plaintext back to
    // the caller. NEVER persisted (not to an account store, not to the
    // project). The caller injects it into the deploy as `agentToken`.
    if (!/^sk-ant-(oat01|api03)-/.test(trimmed)) {
      setError('Token must start with sk-ant-oat01- (OAuth) or sk-ant-api03- (API key).');
      return;
    }
    const mode = /^sk-ant-oat01-/.test(trimmed) ? 'oauth' : 'api';
    onCapture?.({ token: trimmed, mode });
    onClose?.();
  };

  return (
    <MktAlertModal
      open={isOpen}
      onClose={onClose}
      title="Connect Claude"
      ariaLabel="Connect Claude"
      secondaryLabel=""
    >
      <p style={{ color: 'rgba(255,255,255,0.7)', marginTop: 0, lineHeight: 1.5 }}>
        Zibby needs a Claude credential to run your apps&apos; agent. The token
        is used only for this deploy and stored encrypted on the app — it is
        never saved to your account. To set a reusable credential for every
        app in a project, add it in Project Settings &rarr; AI. Pick one:
      </p>
      <div role="radiogroup" aria-label="Claude credential kind" style={{ display: 'flex', gap: 12, margin: '14px 0' }}>
        <label style={radioCard(kind === 'oauth', false)}>
          <input type="radio" name="claude-cred-kind" value="oauth" checked={kind === 'oauth'}
            onChange={() => setKind('oauth')} style={{ marginRight: 8 }} />
          <span style={{ fontWeight: 600 }}>OAuth (recommended)</span>
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)', marginTop: 4 }}>
            Uses your Claude plan.
          </div>
        </label>
        <label style={radioCard(kind === 'api', false)}>
          <input type="radio" name="claude-cred-kind" value="api" checked={kind === 'api'}
            onChange={() => setKind('api')} style={{ marginRight: 8 }} />
          <span style={{ fontWeight: 600 }}>API key</span>
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)', marginTop: 4 }}>
            Pay-per-token via Anthropic.
          </div>
        </label>
      </div>
      <label htmlFor="claude-cred-token" style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
        {kind === 'oauth' ? 'OAuth token' : 'API key'}
      </label>
      <input
        id="claude-cred-token"
        type="password"
        value={token}
        placeholder={kind === 'oauth' ? 'sk-ant-oat01-…' : 'sk-ant-api03-…'}
        autoComplete="off"
        spellCheck={false}
        onChange={(e) => handleTokenChange(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter' && token.trim()) { e.preventDefault(); handleSubmit(); } }}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        style={{ ...inputStyle, borderColor: focused ? 'var(--ion-color-primary)' : 'rgba(255,255,255,0.15)' }}
      />
      {error && <p role="alert" style={{ color: '#f87171', fontSize: 13, marginTop: 8 }}>{error}</p>}
      <div style={{ marginTop: 16 }}>
        <PillButton onClick={handleSubmit} disabled={!token.trim()} className="btn-fullwidth">
          Use for this deploy
        </PillButton>
      </div>
    </MktAlertModal>
  );
}
